import { circuitBreaker, offlineStorage } from '../utils/offlineStorage';

interface RetryOptions {
  maxRetries?: number;
  initialDelay?: number;
  maxDelay?: number;
  shouldRetry?: (error: any) => boolean;
}

const defaultRetryOptions: Required<RetryOptions> = {
  maxRetries: 3,
  initialDelay: 1000,
  maxDelay: 10000,
  shouldRetry: (error: any) => {
    if (!circuitBreaker.canExecute()) {
      console.warn('⚠️ Circuit breaker open - skipping retry');
      return false;
    }

    if (!navigator.onLine) return true;
    if (error?.message?.includes('Failed to fetch')) return true;
    if (error?.message?.includes('network')) return true;
    if (error?.status >= 500) return true;
    return false;
  },
};

export async function supabaseWithRetry<T>(
  operation: () => Promise<{ data: T | null; error: any }>,
  options: RetryOptions = {},
  cacheKey?: string
): Promise<{ data: T | null; error: any; fromCache?: boolean }> {
  const opts = { ...defaultRetryOptions, ...options };
  let lastError: any;

  for (let attempt = 0; attempt < opts.maxRetries; attempt++) {
    try {
      const result = await operation();

      if (!result.error && result.data && cacheKey) {
        offlineStorage.set(cacheKey, result.data, 1000 * 60 * 30);
      }

      return result;
    } catch (error) {
      lastError = error;
      console.warn(`Supabase query attempt ${attempt + 1}/${opts.maxRetries} failed:`, error);

      if (!opts.shouldRetry(error) || attempt === opts.maxRetries - 1) {
        break;
      }

      const delay = Math.min(opts.initialDelay * Math.pow(2, attempt), opts.maxDelay);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  if (cacheKey) {
    const cached = offlineStorage.get<T>(cacheKey);
    if (cached) {
      console.warn('Supabase query failed, returning cached data');
      return { data: cached, error: null, fromCache: true };
    }
  }

  return { data: null, error: lastError };
}
